import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Paper } from "@mui/material";
import { formatDate } from "../../../utils/formatters";
import { CatalogLegendLink } from "./LegendDialog";
import {
  CatalogImportStatusChip,
  CatalogMappingStateChip,
} from "./utils/catalogStatusDisplay";
import { runSummaryStatus, runSummaryTrigger, runSummaryTs } from "./utils/catalogFieldLabels";
import {
  FEED_FORMAT_LEGEND_TITLE,
  GLOBAL_IMPORT_ISSUES_LEGEND_TITLE,
  IMPORT_ISSUES_LEGEND_TITLE,
  IMPORT_STATUS_LEGEND_TITLE,
  MAPPING_STATE_LEGEND_TITLE,
  MAPPING_VALIDATION_LEGEND_TITLE,
} from "./utils/legendsContent";
import { buildSourceTypeSummaryFields } from "./utils/sourceTypeDisplay";
import {
  buildImportRowErrorsSummary,
  buildImportRowWarningsSummary,
  normalizeGlobalErrors,
  normalizeGlobalWarnings,
} from "./utils/importGlobalErrorsDisplay";
import {
  formatSummaryFieldLabel,
  IMPORT_SUMMARY_TABLE_ROWS,
  readSummaryCounter,
} from "./utils/importSummaryCountersDisplay";
import { normalizeMessageList } from "./utils/importMessagesDisplay";

const SummaryTableCell = ({ label, value, legendAction = null }) => (
  <td className="cm-summary-table-cell">
    <span className={`cm-meta-label${legendAction ? " cm-meta-label-with-legend" : ""}`}>
      {label}
      {legendAction}
    </span>
    <span className="cm-meta-value">{value}</span>
  </td>
);

SummaryTableCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  legendAction: PropTypes.node,
};

const SummaryMessageList = ({ title, messages, variant }) => {
  if (!messages.length) {
    return null;
  }

  return (
    <div className="cm-summary-message-section">
      <span className="cm-meta-label">{title}</span>
      <ul className="cm-summary-message-list">
        {messages.map((message, index) => (
          <li key={`${variant}-${index}`} className={`cm-summary-message-line is-${variant}`}>
            {message}
          </li>
        ))}
      </ul>
    </div>
  );
};

SummaryMessageList.propTypes = {
  title: PropTypes.node.isRequired,
  messages: PropTypes.arrayOf(PropTypes.string).isRequired,
  variant: PropTypes.oneOf(["errors", "warnings"]).isRequired,
};

const CatalogVendorSummary = ({
  sourceTypeConfig,
  mappingMeta,
  vendor,
  issues = [],
  warnings = [],
  onOpenMappingLegend,
  onOpenImportLegend,
  onOpenValidationLegend,
  onOpenImportIssuesLegend,
  onOpenGlobalImportLegend,
  onOpenFormatLegend,
}) => {
  const sourceTypeFields = useMemo(
    () => buildSourceTypeSummaryFields(sourceTypeConfig),
    [sourceTypeConfig],
  );

  const importData = mappingMeta?.last_run_summary || vendor?.last_run_summary || {};
  const mappingErrors = useMemo(() => normalizeMessageList(issues), [issues]);
  const mappingWarnings = useMemo(() => normalizeMessageList(warnings), [warnings]);
  const globalImportErrors = useMemo(() => normalizeGlobalErrors(importData), [importData]);
  const globalImportWarnings = useMemo(() => normalizeGlobalWarnings(importData), [importData]);
  const importRowErrors = useMemo(() => buildImportRowErrorsSummary(importData), [importData]);
  const importRowWarnings = useMemo(() => buildImportRowWarningsSummary(importData), [importData]);
  const hasStatusMessages = mappingErrors.length > 0
    || mappingWarnings.length > 0
    || globalImportErrors.length > 0
    || globalImportWarnings.length > 0
    || importRowErrors.length > 0
    || importRowWarnings.length > 0;

  const summaryStatus = runSummaryStatus(importData);
  const summaryTs = runSummaryTs(importData);
  const summaryTrigger = runSummaryTrigger(importData);

  const resolveImportCellValue = (key) => {
    if (!key) {
      return "—";
    }
    if (key === "ts") {
      return summaryTs ? formatDate(summaryTs) : "—";
    }
    if (key === "status") {
      return <CatalogImportStatusChip status={summaryStatus} />;
    }
    if (key === "trigger") {
      return summaryTrigger || "—";
    }
    return readSummaryCounter(importData, key);
  };

  const resolveImportCellLegend = (key) => {
    if (key === "status") {
      return <CatalogLegendLink onClick={onOpenImportLegend} title={IMPORT_STATUS_LEGEND_TITLE} />;
    }
    return null;
  };

  return (
    <Paper className="cm-panel cm-spacer-md cm-vendor-summary">
      <table className="cm-summary-table cm-summary-source-table">
        <tbody>
          <tr>
            <SummaryTableCell
              label="MAPPING"
              legendAction={(
                <CatalogLegendLink onClick={onOpenMappingLegend} title={MAPPING_STATE_LEGEND_TITLE} />
              )}
              value={<CatalogMappingStateChip mappingState={mappingMeta?.mapping_status} />}
            />
            {sourceTypeFields.map((field) => (
              <SummaryTableCell
                key={field.key}
                label={field.label}
                legendAction={field.showFormatLegend ? (
                  <CatalogLegendLink onClick={onOpenFormatLegend} title={FEED_FORMAT_LEGEND_TITLE} />
                ) : null}
                value={field.value}
              />
            ))}
          </tr>
        </tbody>
      </table>

      <div className="cm-summary-divider" aria-hidden="true" />

      <table className="cm-summary-table cm-summary-import-table">
        <tbody>
          {IMPORT_SUMMARY_TABLE_ROWS.map((rowKeys) => (
            <tr key={rowKeys.filter(Boolean).join("-")}>
              {rowKeys.map((key, columnIndex) => (
                <SummaryTableCell
                  key={key || `empty-${columnIndex}`}
                  label={key ? formatSummaryFieldLabel(key) : "\u00A0"}
                  legendAction={resolveImportCellLegend(key)}
                  value={key ? resolveImportCellValue(key) : "\u00A0"}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {hasStatusMessages && (
        <>
          <div className="cm-summary-divider" aria-hidden="true" />
          <SummaryMessageList
            title={(
              <span className="cm-meta-label cm-meta-label-with-legend">
                Mapping errors
                <CatalogLegendLink onClick={onOpenValidationLegend} title={MAPPING_VALIDATION_LEGEND_TITLE} />
              </span>
            )}
            messages={mappingErrors}
            variant="errors"
          />
          <SummaryMessageList
            title={(
              <span className="cm-meta-label cm-meta-label-with-legend">
                Mapping Warning
                <CatalogLegendLink onClick={onOpenValidationLegend} title={MAPPING_VALIDATION_LEGEND_TITLE} />
              </span>
            )}
            messages={mappingWarnings}
            variant="warnings"
          />
          <SummaryMessageList
            title={(
              <span className="cm-meta-label cm-meta-label-with-legend">
                Import errors
                <CatalogLegendLink onClick={onOpenImportIssuesLegend} title={IMPORT_ISSUES_LEGEND_TITLE} />
              </span>
            )}
            messages={importRowErrors}
            variant="errors"
          />
          <SummaryMessageList
            title={(
              <span className="cm-meta-label cm-meta-label-with-legend">
                Import warnings
                <CatalogLegendLink onClick={onOpenImportIssuesLegend} title={IMPORT_ISSUES_LEGEND_TITLE} />
              </span>
            )}
            messages={importRowWarnings}
            variant="warnings"
          />
          <SummaryMessageList
            title={(
              <span className="cm-meta-label cm-meta-label-with-legend">
                Errori globali import
                <CatalogLegendLink onClick={onOpenGlobalImportLegend} title={GLOBAL_IMPORT_ISSUES_LEGEND_TITLE} />
              </span>
            )}
            messages={globalImportErrors}
            variant="errors"
          />
          <SummaryMessageList
            title={(
              <span className="cm-meta-label cm-meta-label-with-legend">
                Warning globali import
                <CatalogLegendLink onClick={onOpenGlobalImportLegend} title={GLOBAL_IMPORT_ISSUES_LEGEND_TITLE} />
              </span>
            )}
            messages={globalImportWarnings}
            variant="warnings"
          />
        </>
      )}
    </Paper>
  );
};

CatalogVendorSummary.propTypes = {
  sourceTypeConfig: PropTypes.shape({
    type: PropTypes.string,
    is_headless: PropTypes.bool,
    detection: PropTypes.string,
    auth: PropTypes.bool,
    auth_method: PropTypes.string,
    gdrive: PropTypes.bool,
  }).isRequired,
  mappingMeta: PropTypes.shape({
    mapping_status: PropTypes.string,
    last_run_state: PropTypes.string,
    last_run_summary: PropTypes.object,
  }).isRequired,
  vendor: PropTypes.shape({
    last_run_state: PropTypes.string,
    last_run_summary: PropTypes.object,
    product_count: PropTypes.number,
  }),
  issues: PropTypes.arrayOf(PropTypes.string),
  warnings: PropTypes.arrayOf(PropTypes.string),
  onOpenMappingLegend: PropTypes.func.isRequired,
  onOpenImportLegend: PropTypes.func.isRequired,
  onOpenValidationLegend: PropTypes.func.isRequired,
  onOpenImportIssuesLegend: PropTypes.func.isRequired,
  onOpenGlobalImportLegend: PropTypes.func.isRequired,
  onOpenFormatLegend: PropTypes.func.isRequired,
};

export default CatalogVendorSummary;
